import type { LoginCodeChannel, SignInCodeChannel, SignInIdentifier } from '@chukua/common';
import { localMobile } from '../../../shared/policies/local-mobile';

export const INVALID_IDENTIFIER_MESSAGE = 'Enter your email or a Kenyan mobile number, like 0712 345 678.';

const PHONE_CHANNEL_NAMES: Record<LoginCodeChannel, string> = { whatsapp: 'WhatsApp', sms: 'SMS' };

const CODE_STEP_TITLES: Record<SignInCodeChannel, string> = {
  email: 'Check your email',
  whatsapp: 'Check WhatsApp',
  sms: 'Check your messages',
};

const SWITCH_LABELS: Record<SignInCodeChannel, string> = {
  email: 'Email it to me instead',
  whatsapp: 'Send it by WhatsApp instead',
  sms: 'Send it by SMS instead',
};

/**
 * Under the start step's field: where the code will go, once what was typed reads as an email or mobile. When the
 * channel isn't the kind typed, the code goes to the account's other contact, so it says so without naming it.
 */
export function sendHint(identifier: SignInIdentifier | null, channel: SignInCodeChannel | null): string | null {
  if (!identifier || !channel) return null;
  if (channel === 'email') {
    return identifier.kind === 'email' ? "We'll send the code by email." : "We'll email the code to the address on this number's account.";
  }
  const by = `We'll send the code by ${PHONE_CHANNEL_NAMES[channel]}`;
  return identifier.kind === 'phone' ? `${by}.` : `${by} to this account's mobile.`;
}

export function codeStepTitle(channel: SignInCodeChannel): string {
  return CODE_STEP_TITLES[channel];
}

export function switchChannelLabel(channel: SignInCodeChannel): string {
  return SWITCH_LABELS[channel];
}

/** The code step's lead, with `target` (what was typed, a mobile as 0712 345 678) set apart so it can be emphasised. */
export interface CodeStepLead {
  before: string;
  target: string;
  after: string;
}

/**
 * Names what was typed, never the account's other contact, and reads the same whether or not anyone has that email
 * or number: the server answers a send alike either way.
 */
export function codeStepLead(identifier: SignInIdentifier, channel: SignInCodeChannel): CodeStepLead {
  const target = identifier.kind === 'phone' ? localMobile(identifier.value) : identifier.value;
  if (channel === 'email') {
    return identifier.kind === 'email'
      ? { before: 'We sent a 6-digit code to ', target, after: '. It expires in 10 minutes.' }
      : { before: 'We emailed a 6-digit code to the address on the account for ', target, after: '.' };
  }
  const by = PHONE_CHANNEL_NAMES[channel];
  return identifier.kind === 'phone'
    ? { before: 'We sent a 6-digit code to ', target, after: ` by ${by}. It expires in 10 minutes.` }
    : { before: `We sent a 6-digit code by ${by} to the mobile number on the account for `, target, after: '.' };
}
